// Pure helpers for read-aloud (no DOM, unit-tested).

/** Chrome silently drops utterances much past ~200 characters. */
export const MAX_CHUNK = 200

/**
 * Split text into speakable chunks: whole sentences, merged up to MAX_CHUNK,
 * and over-long sentences broken at word boundaries.
 */
export function chunkText(text: string, max = MAX_CHUNK): string[] {
  const clean = text.replace(/\s+/g, ' ').trim()
  if (!clean) return []
  const sentences = clean.match(/[^.!?…]+(?:[.!?…]+["'”’)\]]*|$)/g) ?? [clean]

  const pieces: string[] = []
  for (const raw of sentences) {
    const sentence = raw.trim()
    if (!sentence) continue
    if (sentence.length <= max) {
      pieces.push(sentence)
      continue
    }
    let line = ''
    for (const word of sentence.split(' ')) {
      if (line && (line + ' ' + word).length > max) {
        pieces.push(line)
        line = word
      } else {
        line = line ? `${line} ${word}` : word
      }
    }
    if (line) pieces.push(line)
  }

  const chunks: string[] = []
  for (const piece of pieces) {
    const last = chunks[chunks.length - 1]
    if (last && (last + ' ' + piece).length <= max) chunks[chunks.length - 1] = `${last} ${piece}`
    else chunks.push(piece)
  }
  return chunks
}

export const SPEECH_RATES = [1, 1.25, 1.5, 0.8] as const
export type SpeechRate = (typeof SPEECH_RATES)[number]

export function nextRate(rate: number): SpeechRate {
  const i = SPEECH_RATES.indexOf(rate as SpeechRate)
  return SPEECH_RATES[(i + 1) % SPEECH_RATES.length]
}

interface VoiceLike {
  name: string
  lang: string
  localService?: boolean
}

// Natural-sounding English voices, best first, across Chrome, Edge and Safari.
const PREFERRED = [/natural/i, /google us english/i, /samantha/i, /aria/i, /jenny/i, /ava/i, /alex/i]

/** Pick the best available voice for a language like "en-US" (null → browser default). */
export function pickVoice<V extends VoiceLike>(voices: V[], lang: string): V | null {
  const base = lang.toLowerCase().split('-')[0]
  const exact = voices.filter((v) => v.lang.toLowerCase().replace('_', '-') === lang.toLowerCase())
  const sameLang = voices.filter((v) => v.lang.toLowerCase().startsWith(base))
  for (const pool of [exact, sameLang]) {
    for (const pattern of PREFERRED) {
      const hit = pool.find((v) => pattern.test(v.name))
      if (hit) return hit
    }
    if (pool.length) return pool[0]
  }
  return null
}
